import React from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  errors: Record<string, string>;
  submitted: boolean;
  fieldRefs: Record<string, React.RefObject<HTMLElement>>;
  errorMessageOrder?: string[];
};

export const ErrorSummary: React.FC<Props> = ({
  errors,
  submitted,
  fieldRefs,
  errorMessageOrder,
}) => {
  const { t } = useTranslation();
  if (!submitted || Object.keys(errors).length === 0) return null;

  const handleFocus = (field: string) => {
    const ref = fieldRefs[field];
    if (ref && ref.current) {
      ref.current.focus?.();
      if (typeof ref.current.scrollIntoView === 'function') {
        ref.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, field: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleFocus(field);
    }
  };

  const orderedErrors = errorMessageOrder
    ? errorMessageOrder.filter((field) => errors[field])
    : Object.keys(errors).filter((field) => errors[field]);

  if (orderedErrors.length === 0) return null;

  return (
    <div
      className="error-summary"
      role="alert"
      aria-live="assertive"
      style={{
        backgroundColor: '#fdecea',
        border: '1px solid #f5c2c7',
        color: '#b71c1c',
        padding: '16px',
        borderRadius: '4px',
        marginBottom: '32px',
      }}
    >
      <p style={{ margin: 0, marginBottom: '10px' }}>{t('ERRORS.ERROR_SUMMARY_TITLE')}</p>
      <ul style={{ margin: 0, }} role="list">
        {orderedErrors.map((field, index) => (
          <li
            key={index}
            role="button"
            aria-label={`Focus ${field}`}
            tabIndex={0}
            onClick={() => handleFocus(field)}
            onKeyDown={(e) => handleKeyDown(e, field)}
            style={{
              marginBottom:
                orderedErrors.length - 1 === index ? '0' : '8px',
              listStyleType: 'none',
              cursor: 'pointer',
              textDecoration: 'underline',
              outline: 'none',
            }}
          >
            {errors[field]}
          </li>
        ))}
      </ul>
    </div>
  );
};
